import React, { useEffect, useState } from "react";
import { chime } from "../lib/celebrate";
import { Link, useParams } from "react-router-dom";
import { Heart, Trophy, Send, Sparkles, Check, Users, Share2, X, Download } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api, BACKEND_URL, formatApiError } from "../lib/api";

const TOKEN_KEY_PREFIX = "lif_play_";
const NAME_KEY_PREFIX = "lif_name_";
const SIDE_KEY_PREFIX = "lif_side_";

export default function PublicGamePage() {
  const { code } = useParams();
  const upper = (code || "").toUpperCase();
  const [game, setGame] = useState(null);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY_PREFIX + upper) || "");
  const [name, setName] = useState(localStorage.getItem(NAME_KEY_PREFIX + upper) || "");
  const [side, setSide] = useState(localStorage.getItem(SIDE_KEY_PREFIX + upper) || "neutral");
  const [votes, setVotes] = useState({});
  const [drafts, setDrafts] = useState({});
  const [board, setBoard] = useState(null);
  const [results, setResults] = useState({});
  const [streak, setStreak] = useState(0);
  const [confetti, setConfetti] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);

  // My current score from leaderboard
  const myScore = board?.guests?.find((g) => g.name === name)?.score ?? 0;
  const shareCardUrl = token ? `${BACKEND_URL}/api/public/play/${upper}/share-card.png?guest_token=${token}` : `${BACKEND_URL}/api/public/play/${upper}/share-card.png`;
  const playUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/play/${upper}`;

  function shareScore() {
    const text = `I scored ${myScore} pts at ${game?.couple_names || "the wedding"}'s Locked In game! Play yours: ${playUrl}`;
    if (navigator.share) {
      navigator.share({
        title: "Locked In Forever",
        text,
        url: playUrl,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    }
  }
  function copyLink() {
    navigator.clipboard.writeText(playUrl);
    toast.success("Game link copied");
  }

  function loadGame() {
    api.get(`/public/play/${upper}`)
      .then((r) => setGame(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Game not found"));
    api.get(`/public/play/${upper}/leaderboard`).then((r) => setBoard(r.data)).catch(() => {});
  }
  useEffect(() => { loadGame(); }, [upper]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token || !game) return;
    const t = setInterval(() => {
      api.get(`/public/play/${upper}/leaderboard`).then((r) => setBoard(r.data)).catch(() => {});
      api.get(`/public/play/${upper}`).then((r) => setGame(r.data)).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [token, game, upper]);

  // Fetch results for revealed questions
  useEffect(() => {
    if (!game) return;
    (async () => {
      const out = {};
      for (const idx of (game.revealed_indices || [])) {
        const qid = game.questions[idx]?.question_id;
        if (!qid) continue;
        try {
          const { data } = await api.get(`/public/play/${upper}/results/${qid}`);
          if (data.revealed) out[qid] = data;
        } catch {}
      }
      setResults(out);
    })();
  }, [game, upper]);

  async function join(e) {
    e.preventDefault();
    if (!name.trim()) return toast.error("What should we call you?");
    try {
      const { data } = await api.post(`/public/play/${upper}/join`, { name: name.trim(), side });
      localStorage.setItem(TOKEN_KEY_PREFIX + upper, data.guest_token);
      localStorage.setItem(NAME_KEY_PREFIX + upper, name.trim());
      localStorage.setItem(SIDE_KEY_PREFIX + upper, side);
      setToken(data.guest_token);
      toast.success(`Welcome, ${name}!`);
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function vote(qid) {
    const ans = (drafts[qid] || "").trim();
    if (!ans) return toast.error("Type your answer");
    try {
      const { data } = await api.post(`/public/play/${upper}/vote`, {
        guest_token: token, question_id: qid, answer: ans,
      });
      setVotes((v) => ({ ...v, [qid]: ans }));
      if ((game.revealed_indices || []).includes(game.questions.findIndex((q) => q.question_id === qid))) {
        api.get(`/public/play/${upper}/results/${qid}`).then((r) => setResults((x) => ({ ...x, [qid]: r.data }))).catch(() => {});
      }
      chime(data.correct);
      if (data.correct) {
        toast.success("Correct! +10 pts");
        setStreak((s) => s + 1);
        setConfetti((c) => c + 1);
      } else {
        toast("Submitted");
        setStreak(0);
      }
    } catch (err) { toast.error(formatApiError(err)); }
  }

  function partnerName(role) {
    const names = (game?.couple_names || "").split(/\s*&\s*/);
    if (role === "partner_one" && names[0]) return names[0];
    if (role === "partner_two" && names[1]) return names[1];
    return (role || "couple").replace("_", " ");
  }

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-warm)] p-6">
      <div className="text-center">
        <p className="font-script text-[var(--gold)] text-4xl">Oops</p>
        <p className="font-serif text-3xl mt-1" data-testid="game-error">{error === "Game not found" ? "We couldn't find that game code." : error}</p>
        <p className="text-sm text-[var(--muted)] mt-2">Double-check the code on the couple's screen, or try our demo game.</p>
        <div className="flex flex-wrap gap-2 justify-center mt-6">
          <a href="/play/DEMO26" className="btn-gold" data-testid="game-error-demo">Play the demo game</a>
          <Link to="/" className="btn-outline-gold">Back to home</Link>
        </div>
      </div>
    </div>
  );
  if (!game) return <div className="min-h-screen flex items-center justify-center"><p className="text-[var(--muted)]">Loading…</p></div>;

  const closed = !game.is_open;

  // Join screen
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[var(--bg-warm)] via-[var(--bg)] to-[var(--bg-warm)] flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl p-8 md:p-10 max-w-md w-full border border-[var(--gold-line)]" data-testid="join-card">
          <div className="text-center mb-6">
            <Heart className="w-8 h-8 mx-auto text-[var(--gold)] mb-3" />
            <p className="font-script text-3xl text-[var(--gold)]">{game.couple_names}</p>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted)] mt-1">{game.mode.replaceAll("_", " ")}</p>
            <h1 className="font-serif text-3xl mt-3">{game.title}</h1>
            {game.description && <p className="text-sm text-[var(--muted)] mt-2">{game.description}</p>}
            <p className="text-xs text-[var(--muted)] mt-3 flex items-center gap-1 justify-center"><Users className="w-3 h-3" /> {game.guest_count} playing</p>
          </div>

          {closed ? (
            <p className="text-center text-sm text-[var(--muted)]">This game is closed.</p>
          ) : (
            <form onSubmit={join} className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Your name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Aunt Pat" className="w-full mt-1 border border-[var(--line)] rounded-xl px-4 py-3" data-testid="join-name" required />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mb-2 block">I'm with…</label>
                <div className="grid grid-cols-3 gap-2">
                  {["bride", "groom", "neutral"].map((s) => (
                    <button type="button" key={s} onClick={() => setSide(s)} data-testid={`side-${s}`} className={`text-xs px-3 py-2 rounded-xl border capitalize transition ${side === s ? "bg-[var(--gold)] text-white border-[var(--gold)]" : "border-[var(--line)]"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <button type="submit" data-testid="join-submit" className="btn-gold w-full justify-center">Join the game</button>
            </form>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[var(--bg-warm)] to-[var(--bg)]" data-testid="public-game">
      {/* Confetti */}
      <AnimatePresence>
        {confetti > 0 && (
          <motion.div
            key={confetti}
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 1.4 }}
            onAnimationComplete={() => setConfetti(0)}
            className="fixed inset-0 pointer-events-none z-50"
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="font-script text-7xl text-[var(--gold)]">+10!</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="container-wide max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Header */}
        <div className="bg-[var(--ink)] text-white rounded-3xl p-6 md:p-7">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-script text-2xl text-[var(--gold)]">{game.couple_names}</p>
              <p className="font-serif text-xl mt-1">{game.title}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/50">You</p>
              <p className="font-medium">{name}</p>
              <p className="text-xs text-[var(--gold)] capitalize">{side} side</p>
            </div>
          </div>
          {streak >= 2 && (
            <p className="mt-3 text-sm text-[var(--gold)] flex items-center gap-1"><Sparkles className="w-4 h-4" /> {streak}-streak — keep it going!</p>
          )}
        </div>

        {/* Leaderboard mini */}
        {board && board.guests.length > 0 && (
          <div className="bg-white rounded-2xl p-4 border border-[var(--line)]">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-2 flex items-center gap-1"><Trophy className="w-3 h-3" /> Top 3</p>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              {board.guests.slice(0, 3).map((g, i) => (
                <div key={i} className={`p-3 rounded-xl ${i === 0 ? "bg-[#C8A96B]/15 border border-[var(--gold-line)]" : "bg-[var(--bg-warm)]"}`}>
                  <p className="font-serif text-xl">{i === 0 ? "👑" : `#${i + 1}`}</p>
                  <p className="font-medium truncate">{g.name}</p>
                  <p className="text-xs text-[var(--muted)]">{g.score} pts</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Questions */}
        {closed && <p className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 text-center">This game is closed for new votes — but you can still see results.</p>}
        <div className="space-y-4">
          {game.questions.map((q, idx) => {
            const isRevealed = (game.revealed_indices || []).includes(idx);
            const r = results[q.question_id];
            const myVote = votes[q.question_id];
            // Answers stay hidden until this guest has guessed (or the game is closed).
            const showResults = isRevealed && (myVote || closed);
            return (
              <motion.div
                key={q.question_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
                className="bg-white rounded-2xl p-5 border border-[var(--line)]"
                data-testid={`q-${q.question_id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Q{idx + 1}</p>
                  {showResults && <span className="text-[10px] uppercase tracking-[0.18em] text-green-700">Revealed</span>}
                </div>
                <p className="font-serif text-lg mb-3">{q.text}</p>
                {!closed && (!isRevealed || !myVote) && (
                  myVote ? (
                    <p className="text-sm text-[var(--gold)] flex items-center gap-1"><Check className="w-3 h-3" /> Submitted: <span className="italic text-[var(--ink-soft)]">"{myVote}"</span></p>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        value={drafts[q.question_id] ?? ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [q.question_id]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && vote(q.question_id)}
                        placeholder="Your guess…"
                        className="flex-1 border border-[var(--line)] rounded-xl px-4 py-2 text-sm bg-[var(--bg-warm)]"
                        data-testid={`vote-input-${q.question_id}`}
                      />
                      <button onClick={() => vote(q.question_id)} data-testid={`vote-${q.question_id}`} className="w-10 h-10 rounded-full bg-[var(--gold)] text-white flex items-center justify-center"><Send className="w-4 h-4" /></button>
                    </div>
                  )
                )}
                {showResults && r && (
                  <div className="space-y-2 mt-3">
                    {r.couple_answers?.length > 0 && (
                      <div className="p-3 bg-[#C8A96B]/10 rounded-xl">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Couple's answer</p>
                        {r.couple_answers.map((ca, i) => (
                          <p key={i} className="text-sm">{partnerName(ca.partner_role)}: <strong>{ca.answer}</strong></p>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Vote distribution ({r.total_votes})</p>
                    {r.groups.map((g, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm bg-[var(--bg-warm)] rounded-xl p-2.5">
                        <span className="flex-1 truncate">{g.answer}</span>
                        <span className="text-xs px-2 py-0.5 bg-white rounded-full">{g.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
        <p className="text-center text-xs text-[var(--muted)] py-6">
          Powered by <Link to="/" className="text-[var(--gold)] underline">LockedInForever</Link>
        </p>
      </div>

      {/* Floating share button — bottom-left to avoid platform badge on desktop, high z-index */}
      <button
        onClick={() => setShareOpen(true)}
        data-testid="open-share"
        className="fixed bottom-6 left-6 z-[9999] px-5 py-3 rounded-full bg-[var(--gold)] text-white shadow-xl flex items-center gap-2 hover:scale-105 transition"
      >
        <Share2 className="w-4 h-4" /> Share my score
      </button>

      {/* Share sheet */}
      <AnimatePresence>
        {shareOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShareOpen(false)}
            className="fixed inset-0 bg-black/70 z-50 flex items-end md:items-center justify-center p-4 md:p-6"
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl w-full max-w-md p-5 md:p-6 max-h-[90vh] overflow-y-auto"
              data-testid="share-sheet"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="font-serif text-2xl">Share your moment</p>
                <button onClick={() => setShareOpen(false)}><X className="w-5 h-5" /></button>
              </div>
              <p className="text-sm text-[var(--muted)] mb-4">Drop this on your story — auto-watermarked with the official logo.</p>
              <img
                src={shareCardUrl}
                alt="Locked In share card"
                data-testid="share-card-preview"
                className="w-full rounded-2xl mb-4 aspect-[9/16] object-cover bg-[var(--bg-warm)]"
              />
              <div className="grid grid-cols-2 gap-3">
                <button onClick={shareScore} data-testid="share-native" className="btn-gold w-full justify-center">
                  <Share2 className="w-4 h-4" /> Share
                </button>
                <a href={shareCardUrl} download={`locked-in-${upper}.png`} data-testid="share-download" className="btn-outline-gold w-full justify-center">
                  <Download className="w-4 h-4" /> Save
                </a>
                <button onClick={copyLink} data-testid="share-copy-link" className="btn-outline-gold w-full justify-center col-span-2">
                  Copy game link
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
